import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { formatDate } from "date-fns"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import {
  useWaitingRoomFilters,
  useWaitingRoomFiltersContext,
} from "@/modules/waiting-room/contexts/waiting-room-filters-context"
import { checkStatusesIsCancelledOrClosed } from "@/modules/waiting-room/helpers/waiting-room-helper"
import { AppointmentStatus } from "@/types/__generated__/union-types"

const formatDateForFilter = (date: Date) => formatDate(date, "yyyy-MM-dd")

const STATUS: AppointmentStatus[] = ["OPEN", "IN_PROGRESS", "CLOSED", "CANCELLED"]

const StatusFilter = () => {
  const { t } = useTranslation()

  const { filters } = useWaitingRoomFilters()
  const setFilters = useWaitingRoomFiltersContext((state) => state.setFilters)

  const activeFilters = filters.statuses || []
  const activeFilterCount = activeFilters.length

  const handleCheckChange = (item: AppointmentStatus) => {
    const index = activeFilters.indexOf(item)
    const newActiveFilters = [...activeFilters]

    if (index < 0) newActiveFilters.push(item)
    else newActiveFilters.splice(index, 1)

    if (newActiveFilters.length > 0) setFilters({ statuses: newActiveFilters })
    else setFilters({ statuses: null })

    handleSetDateRange(newActiveFilters)
  }

  const handleResetStatus = () => {
    setFilters({ statuses: null })
  }

  const handleSetDateRange = (newActiveFilters: AppointmentStatus[]) => {
    const activeFiltersDateRange = filters.dateRange
    const today = new Date()

    if (!activeFiltersDateRange && checkStatusesIsCancelledOrClosed(newActiveFilters)) {
      setFilters({
        dateRange: { from: formatDateForFilter(today), to: formatDateForFilter(today) },
      })
    }
  }

  const getStatusLabel = (statusKey: string, activeFilterCount: number): string => {
    const statusLabel = t(statusKey)
    const filterCount = activeFilterCount > 0 ? ` (${activeFilterCount})` : ""
    return `${statusLabel}${filterCount}`
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        data-testid="status-filter"
        className="aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer">
        <Chip className="h-8 gap-0.5" checked={activeFilterCount > 0} type="button">
          <div
            className={cn("hidden w-5 overflow-hidden transition-all duration-200", activeFilterCount > 0 && "block")}>
            <Check size={20} />
          </div>
          <span className="px-1.5">
            {getStatusLabel("scheduling.waitingRoom.grid.columnName.status", activeFilterCount)}
          </span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        className="absolute -top-1 grid gap-0.5 rounded-md border-none p-2 leading-5">
        {STATUS.map((status: AppointmentStatus) => (
          <DropdownMenuCheckboxItem
            key={status}
            data-testid={`status-filter-option-${status.toLowerCase()}`}
            onSelect={(e) => e.preventDefault()}
            className="h-9"
            checked={activeFilters.includes(status)}
            onCheckedChange={() => handleCheckChange(status)}>
            {t(`scheduling.waitingRoom.status.${status}`)}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator className="bg-border -mx-2 my-2" />
        <div className="flex justify-end">
          <Button
            onClick={handleResetStatus}
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { StatusFilter }
